/**
 * pdf-signature-verify.js
 * ============================================================
 * PDF 数字签名密码学验证模块（纯前端，零后端）
 *
 * 功能：解析 PDF 签名域 / CMS(PKCS#7) / X.509 证书链，并完成六项密码学验证：
 *   ① PDF 数字签名有效性     （RSA-PKCS1v15 / SHA-256，覆盖 ByteRange 内容）
 *   ② messageDigest 比对      （签名属性中的摘要 vs 重新计算摘要）
 *   ③ 终端证书 ← 中级证书 签发签名
 *   ④ 中级证书 ← 根证书 签发签名
 *   ⑤ AKI/SKI 链式关联        （三级逐级衔接）
 *   ⑥ 根证书与沃通官方公开根一致性
 *
 * 依赖：asn1js@3.x（npm 安装，已按 3.0.5 API 编写）
 *   npm i asn1js@3.0.5
 *
 * 验签实现：
 *   - 默认使用浏览器 WebCrypto（crypto.subtle，需 secure context，如 localhost/https）
 *   - 内置纯 JS 回退（同步 SHA-256 + 大整数 RSA-PKCS1v15），file:// 或 WebCrypto 不可用时自动生效
 *
 * 用法（Vue / 任意 JS 工程）：
 *   import { verifyPdfSignature, verifyPdfSignatureByFile } from './pdf-signature-verify.js';
 *
 *   // 方式一：直接传 ArrayBuffer
 *   const buf = await (await fetch('xxx.pdf')).arrayBuffer();
 *   const result = await verifyPdfSignature(new Uint8Array(buf), { forcePureJs: false });
 *
 *   // 方式二：传 File 对象（<input type="file"> 的 .files[0]）
 *   const result = await verifyPdfSignatureByFile(file, { forcePureJs: false });
 *
 * 返回结构：
 *   {
 *     report:    [{ key, pass, detail }, ...]        // 六项（及以上）验证明细
 *     chain:     [{ role, subject, issuer, serial, validFrom, validTo,
 *                   isCA, pathlen, sha256, isSelfSigned, ski, aki }, ...]
 *     signerMeta:{ reason, location, contact, time, filter, subFilter }
 *     byteRange: [a, b, c, d]
 *     certCount: 证书张数
 *   }
 * ============================================================
 */
import * as asn1js from 'asn1js';

/* ------------------------------------------------------------------ */
/* 常量                                                                */
/* ------------------------------------------------------------------ */
const OID = {
  rsaEncryption: '1.2.840.113549.1.1.1',
  signedData: '1.2.840.113549.1.7.2',
  messageDigest: '1.2.840.113549.1.9.4',
  basicConstraints: '2.5.29.19',
  keyUsage: '2.5.29.15',
  extKeyUsage: '2.5.29.37',
  subjectKeyId: '2.5.29.14',
  authKeyId: '2.5.29.35',
};
const NAME_SHORT = {
  '2.5.4.3': 'CN', '2.5.4.10': 'O', '2.5.4.11': 'OU',
  '2.5.4.6': 'C', '2.5.4.7': 'L', '2.5.4.8': 'ST',
  '1.2.840.113549.1.9.1': 'emailAddress',
};
// 沃通(WebTrust/Adobe 可信) 根证书 CA WoTrus Root 的 SHA-256 指纹（官方公布值）
const WOTRUS_ROOT_SHA256 = 'F2A39E3514AEAA48716E93F0CB776BEBC88E9DE415C0C1A0E3C36AD01AA06751';

/* ------------------------------------------------------------------ */
/* 通用工具                                                            */
/* ------------------------------------------------------------------ */
function bytesToHex(u8) {
  return Array.from(u8 || []).map(b => b.toString(16).padStart(2, '0')).join('');
}
function hexToBytes(hex) {
  const c = hex.replace(/\s+/g, '');
  const o = new Uint8Array(c.length / 2);
  for (let i = 0; i < o.length; i++) o[i] = parseInt(c.substr(i * 2, 2), 16);
  return o;
}
function u8toLatin1(u8) {
  // 分块转换，避免 String.fromCharCode.apply 在大型 Uint8Array 上栈溢出
  const chunks = [];
  for (let i = 0; i < u8.length; i += 8192) {
    chunks.push(String.fromCharCode.apply(null, u8.subarray(i, i + 8192)));
  }
  return chunks.join('');
}
function bytesOf(el) {
  // asn1js 3.x 用 valueHexView(Uint8Array)，兼容旧版 valueHex(ArrayBuffer)
  return el.valueBlock.valueHexView || el.valueBlock.valueHex || new Uint8Array(0);
}
function getOidString(oidEl) {
  return oidEl.valueBlock.toString();
}
async function sha256(u8) {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', u8));
}
/** 还原 PDF 字面量字符串转义：\034(八进制) 与 \n \r \t \b \f \\ \( \) */
function unescapePdfString(s) {
  let o = s.replace(/\\([0-7]{1,3})/g, (m, oct) => String.fromCharCode(parseInt(oct, 8)));
  return o.replace(/\\([nrtbf\\()])/g, (m, ch) => (
    { n: '\n', r: '\r', t: '\t', b: '\b', f: '\f', '\\': '\\', '(': '(', ')': ')' }[ch]
  ));
}
/** 若带 UTF-16BE BOM(FE FF) 则按 UTF-16BE 解码 */
function decodeUtf16BEIfNeeded(s) {
  const b = s.split('').map(ch => ch.charCodeAt(0) & 0xff);
  if (b[0] === 0xfe && b[1] === 0xff) {
    let out = '';
    for (let i = 2; i + 1 < b.length; i += 2) out += String.fromCharCode((b[i] << 8) | b[i + 1]);
    return out;
  }
  return s;
}

/* ------------------------------------------------------------------ */
/* 纯 JS 密码学回退（WebCrypto 不可用 / file:// 场景）                  */
/* ------------------------------------------------------------------ */
const SHA256_K = new Uint32Array([
  0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
  0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
  0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
  0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
  0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
  0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
  0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
  0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2,
]);
const rotr = (x, n) => (x >>> n) | (x << (32 - n));
const ch = (x, y, z) => (x & y) ^ (~x & z);
const maj = (x, y, z) => (x & y) ^ (x & z) ^ (y & z);
const bsig0 = x => rotr(x, 2) ^ rotr(x, 13) ^ rotr(x, 22);
const bsig1 = x => rotr(x, 6) ^ rotr(x, 11) ^ rotr(x, 25);
const ssig0 = x => rotr(x, 7) ^ rotr(x, 18) ^ (x >>> 3);
const ssig1 = x => rotr(x, 17) ^ rotr(x, 19) ^ (x >>> 10);

/** 同步 SHA-256（纯 JS，WebCrypto 不可用时的回退） */
function sha256Sync(input) {
  const msg = new Uint8Array(input);
  const l = msg.length;
  const paddedLen = (((l + 8) >> 6) + 1) << 6;
  const p = new Uint8Array(paddedLen);
  p.set(msg);
  p[l] = 0x80;
  const dv = new DataView(p.buffer);
  dv.setUint32(paddedLen - 8, Math.floor(l / 0x20000000));
  dv.setUint32(paddedLen - 4, (l << 3) >>> 0);
  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a,
      h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;
  const w = new Uint32Array(64);
  for (let i = 0; i < paddedLen; i += 64) {
    for (let t = 0; t < 16; t++) w[t] = dv.getUint32(i + t * 4);
    for (let t = 16; t < 64; t++) {
      const s0 = ssig0(w[t - 15]), s1 = ssig1(w[t - 2]);
      w[t] = (w[t - 16] + s0 + w[t - 7] + s1) >>> 0;
    }
    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
    for (let t = 0; t < 64; t++) {
      const t1 = (h + bsig1(e) + ch(e, f, g) + SHA256_K[t] + w[t]) >>> 0;
      const t2 = (bsig0(a) + maj(a, b, c)) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0; h5 = (h5 + f) >>> 0; h6 = (h6 + g) >>> 0; h7 = (h7 + h) >>> 0;
  }
  const out = new Uint8Array(32), odv = new DataView(out.buffer);
  odv.setUint32(0, h0); odv.setUint32(4, h1); odv.setUint32(8, h2); odv.setUint32(12, h3);
  odv.setUint32(16, h4); odv.setUint32(20, h5); odv.setUint32(24, h6); odv.setUint32(28, h7);
  return out;
}
function bytesToBigInt(u8) {
  let h = '';
  for (const b of u8) h += b.toString(16).padStart(2, '0');
  return BigInt('0x' + (h || '0'));
}
function bigIntToBytes(n, len) {
  const hex = n.toString(16);
  const full = hex.padStart(len * 2, '0').slice(-len * 2);
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i++) out[i] = parseInt(full.substr(i * 2, 2), 16);
  return out;
}
function modPow(base, exp, mod) {
  let r = 1n;
  base %= mod;
  while (exp > 0n) {
    if (exp & 1n) r = (r * base) % mod;
    base = (base * base) % mod;
    exp >>= 1n;
  }
  return r;
}
/** SHA-256 的 DigestInfo 前缀（EMSA-PKCS1-v1_5） */
const DI_SHA256 = [0x30, 0x31, 0x30, 0x0d, 0x06, 0x09, 0x60, 0x86, 0x48, 0x01, 0x65, 0x03, 0x04, 0x02, 0x01, 0x05, 0x00, 0x04, 0x20];

/** 纯 JS RSA-PKCS1v15 验签 */
function rsaVerifyPKCS1v15(nBytes, eBytes, sigBytes, dataBytes) {
  while (nBytes.length > 1 && nBytes[0] === 0x00) nBytes = nBytes.slice(1); // 去掉 DER 前导零
  const n = bytesToBigInt(nBytes), e = bytesToBigInt(eBytes);
  const k = nBytes.length, sig = bytesToBigInt(sigBytes);
  if (sig >= n) return false;
  const m = modPow(sig, e, n);
  const em = bigIntToBytes(m, k);
  if (em[0] !== 0x00 || em[1] !== 0x01) return false;
  const di = sha256Sync(dataBytes);
  const tail = new Uint8Array(DI_SHA256.length + 32);
  tail.set(DI_SHA256); tail.set(di, DI_SHA256.length);
  let i = 2;
  while (i < em.length && em[i] === 0xff) i++;
  if (i >= em.length || em[i] !== 0x00) return false;
  const t = em.slice(i + 1);
  if (t.length !== tail.length) return false;
  for (let j = 0; j < t.length; j++) if (t[j] !== tail[j]) return false;
  return true;
}
/** 从 SPKI(DER) 中提取 RSA n/e */
function parseRsaKeyFromSpki(spkiDer) {
  const sp = asn1js.fromBER(spkiDer).result;
  let bitStr = new Uint8Array(bytesOf(sp.valueBlock.value[1]));
  if (bitStr[0] === 0x00) bitStr = bitStr.slice(1); // 去掉 unused-bits 字节(如有)
  const rsap = asn1js.fromBER(bitStr).result;
  return {
    n: new Uint8Array(bytesOf(rsap.valueBlock.value[0])),
    e: new Uint8Array(bytesOf(rsap.valueBlock.value[1])),
  };
}

/* ------------------------------------------------------------------ */
/* 1. 从 PDF 原始字节提取签名                                           */
/* ------------------------------------------------------------------ */
function extractSignatureFromPdf(rawBytes) {
  const pdfStr = u8toLatin1(rawBytes);
  const brM = /\/ByteRange\s*\[\s*(\d+)\s+(\d+)\s+(\d+)\s+(\d+)\s*\]/.exec(pdfStr);
  if (!brM) throw new Error('未找到 /ByteRange（可能不是带签名的 PDF）');
  const byteRange = [brM[1], brM[2], brM[3], brM[4]].map(Number);

  // 找 /Contents 后的长 hex（签名数据通常 > 1000 hex 字符）
  const contRe = /\/Contents\s*<([0-9a-fA-F\s]+)>/g;
  let m, contentsHex = null;
  while ((m = contRe.exec(pdfStr)) !== null) {
    const h = m[1].replace(/\s+/g, '');
    if (h.length > 1000) { contentsHex = h; break; }
  }
  if (!contentsHex) throw new Error('未找到 /Contents 签名数据');
  const contents = hexToBytes(contentsHex);

  const grab = re => {
    const x = re.exec(pdfStr);
    return x ? decodeUtf16BEIfNeeded(unescapePdfString(x[1])) : null;
  };
  return {
    byteRange,
    contents,
    meta: {
      filter: grab(/\/Filter\s*\/([A-Za-z0-9._]+)/),
      subFilter: grab(/\/SubFilter\s*\/([A-Za-z0-9._]+)/),
      reason: grab(/\/Reason\s*\((.*?)\)/s),
      location: grab(/\/Location\s*\((.*?)\)/s),
      contact: grab(/\/ContactInfo\s*\((.*?)\)/s),
      m: grab(/\/M\s*\(D:([0-9+']+)\)/),
    },
  };
}

/* ------------------------------------------------------------------ */
/* 2. CMS(PKCS#7) / X.509 解析                                         */
/* ------------------------------------------------------------------ */
function parseSignedData(cmsDer) {
  const ber = asn1js.fromBER(cmsDer);
  if (!(ber.result instanceof asn1js.BaseBlock)) throw new Error('CMS DER 解析失败');
  const contentInfo = ber.result;
  const ctype = getOidString(contentInfo.valueBlock.value[0]);
  if (ctype !== OID.signedData) throw new Error('非 SignedData: ' + ctype);
  const sd = contentInfo.valueBlock.value[1].valueBlock.value[0];
  // certificates: [0] Constructed（asn1js 3.x 中 context-specific tagClass=3）
  const certsEl = sd.valueBlock.value.find(el => el.idBlock.tagClass === 3 && el.idBlock.tagNumber === 0);
  const certsRaw = certsEl ? certsEl.valueBlock.value : [];
  // signerInfos：最后一个 Set
  const sets = sd.valueBlock.value.filter(el => el.constructor.name === 'Set');
  const signerInfo = sets.length ? sets[sets.length - 1].valueBlock.value[0] : null;
  return { certsRaw, signerInfo };
}
function parseCert(certEl) {
  const tbs = certEl.valueBlock.value[0], sigVal = certEl.valueBlock.value[2];
  const tbsRaw = new Uint8Array(tbs.valueBeforeDecode);
  // asn1js 3.x 的 BIT STRING valueHexView 不含 unused-bits 字节
  const sigRaw = new Uint8Array(bytesOf(sigVal));
  const vals = tbs.valueBlock.value;
  let idx = 0;
  if (vals[idx] && vals[idx].idBlock.tagClass === 3 && vals[idx].idBlock.tagNumber === 0) idx++; // [0] version
  const serial = new Uint8Array(bytesOf(vals[idx++]));
  vals[idx++];                                      // signature
  const issuer = vals[idx++];
  const validity = vals[idx++];
  const subject = vals[idx++];
  const spki = vals[idx++];
  let extensions = null;
  if (idx < vals.length && vals[idx].idBlock.tagClass === 3 && vals[idx].idBlock.tagNumber === 3) {
    const inner = vals[idx].valueBlock.value[0];
    extensions = inner ? inner.valueBlock.value : null; // SEQUENCE OF Extension
  }
  return { certEl, tbsRaw, sigRaw, serial, issuer, validity, subject, spki, extensions };
}
function nameToString(nameEl) {
  if (!nameEl) return '';
  const parts = [];
  for (const rdn of nameEl.valueBlock.value) {
    for (const atv of rdn.valueBlock.value) {
      const oid = getOidString(atv.valueBlock.value[0]);
      const val = atv.valueBlock.value[1];
      const nm = val.constructor.name;
      let s = '';
      if (nm === 'Utf8String' || nm === 'PrintableString' || nm === 'IA5String') s = val.valueBlock.value;
      else if (nm === 'BmpString') s = String.fromCharCode(...bytesOf(val));
      else s = bytesToHex(bytesOf(val));
      parts.push((NAME_SHORT[oid] || oid) + '=' + s);
    }
  }
  return parts.join(', ');
}
function parseTime(el) {
  const children = el.valueBlock && Array.isArray(el.valueBlock.value) ? el.valueBlock.value : [];
  const isTime = x => x && (x.constructor.name === 'UTCTime' || x.constructor.name === 'GeneralizedTime');
  let t = el;
  if (children.length && isTime(children[0])) t = children[0];
  if (isTime(t)) {
    try { return t.toDate().toISOString(); } catch (e) { return String(t.valueBlock.value || ''); }
  }
  return String((t.valueBlock && t.valueBlock.value) || '');
}
function parseExtensions(extensions) {
  const ext = { basicConstraints: null, keyUsage: [], eku: [], ski: null, aki: null };
  if (!extensions) return ext;
  for (const extEl of extensions) {
    const oid = getOidString(extEl.valueBlock.value[0]);
    const valBytes = new Uint8Array(bytesOf(extEl.valueBlock.value[extEl.valueBlock.value.length - 1]));
    try {
      const val = asn1js.fromBER(valBytes).result;
      if (oid === OID.basicConstraints) {
        const ca = val.valueBlock.value.filter(x => x.constructor.name === 'Boolean' && x.valueBlock.value === true).length > 0;
        const pl = val.valueBlock.value.find(x => x.constructor.name === 'Integer');
        ext.basicConstraints = { ca, pathlen: pl ? Number(pl.valueBlock.value) : null };
      } else if (oid === OID.keyUsage) {
        const bits = bytesOf(val);
        ext.keyUsage = { digitalSignature: !!(bits[0] & 0x80), keyCertSign: !!(bits[0] & 0x04), cRLSign: !!(bits[0] & 0x02) };
      } else if (oid === OID.extKeyUsage) {
        ext.eku = val.valueBlock.value.map(o => getOidString(o));
      } else if (oid === OID.subjectKeyId) {
        ext.ski = bytesToHex(bytesOf(val));
      } else if (oid === OID.authKeyId) {
        // AuthorityKeyIdentifier ::= SEQUENCE { keyIdentifier [0] IMPLICIT OCTET STRING OPTIONAL, ... }
        const keyid = val.valueBlock.value.find(x => x.idBlock.tagClass === 3 && x.idBlock.tagNumber === 0);
        ext.aki = keyid ? bytesToHex(bytesOf(keyid)) : null;
      }
    } catch (e) { /* 忽略无法解析的扩展 */ }
  }
  return ext;
}
function parseSignerInfo(si) {
  const vals = si.valueBlock.value;
  let idx = 0;
  idx++;                                        // version
  const sidEl = vals[idx++];
  let sid = null;
  if (sidEl.idBlock.tagClass === 3 && sidEl.idBlock.tagNumber === 0) {
    sid = { type: 'ski', value: bytesToHex(bytesOf(sidEl.valueBlock.value[0])) };
  } else {
    sid = {
      type: 'issuerAndSerial',
      issuer: nameToString(sidEl.valueBlock.value[0]),
      serial: bytesToHex(bytesOf(sidEl.valueBlock.value[1])),
    };
  }
  idx++;                                        // digestAlgorithm
  let signedAttrs = null, signedAttrsDer = null;
  if (vals[idx] && vals[idx].idBlock.tagClass === 3 && vals[idx].idBlock.tagNumber === 0) {
    const saEl = vals[idx++];
    signedAttrs = saEl;
    // [0] IMPLICIT SET -> 还原为 SET(0x31) 标签，即签名覆盖的 SignedAttributes DER
    const raw = new Uint8Array(saEl.valueBeforeDecode.slice());
    raw[0] = 0x31;
    signedAttrsDer = raw;
  }
  const sigAlgoOid = getOidString(vals[idx++].valueBlock.value[0]);
  const signature = new Uint8Array(bytesOf(vals[idx++]));
  return { sid, signedAttrs, signedAttrsDer, sigAlgoOid, signature };
}
function parseMessageDigest(si) {
  if (!si.signedAttrs) return null;
  for (const a of si.signedAttrs.valueBlock.value) {
    if (getOidString(a.valueBlock.value[0]) === OID.messageDigest) {
      const val = a.valueBlock.value[a.valueBlock.value.length - 1].valueBlock.value[0];
      return new Uint8Array(bytesOf(val));
    }
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* 3. 验签：WebCrypto 优先，纯 JS 回退                                  */
/* ------------------------------------------------------------------ */
async function verifyRSA(spkiDer, sig, data, forcePureJs) {
  if (!forcePureJs && typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const key = await crypto.subtle.importKey(
        'spki', spkiDer, { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, false, ['verify'],
      );
      return await crypto.subtle.verify({ name: 'RSASSA-PKCS1-v1_5' }, key, sig, data);
    } catch (e) { /* 回退到纯 JS */ }
  }
  const { n, e } = parseRsaKeyFromSpki(spkiDer);
  return rsaVerifyPKCS1v15(n, e, sig, data);
}

/* ------------------------------------------------------------------ */
/* 4. 主验证流程                                                       */
/* ------------------------------------------------------------------ */
/**
 * 验证 PDF 数字签名与证书链
 * @param {Uint8Array} rawBytes   PDF 文件字节
 * @param {object} [options]
 * @param {boolean} [options.forcePureJs=false]  强制纯 JS 验签（跳过 WebCrypto）
 * @param {string}  [options.rootSha256=WOTRUS]  内置可信根 SHA-256 指纹（用于第⑥项比对）
 * @returns {Promise<{report:Array, chain:Array, signerMeta:object, byteRange:Array, certCount:number}>}
 */
export async function verifyPdfSignature(rawBytes, options = {}) {
  const { forcePureJs = false, rootSha256 = WOTRUS_ROOT_SHA256 } = options;
  const report = [];
  const ok = (key, detail) => report.push({ key, pass: true, detail });
  const fail = (key, detail) => report.push({ key, pass: false, detail });

  let sig;
  try {
    sig = extractSignatureFromPdf(rawBytes);
  } catch (e) {
    fail('extract', e.message);
    return { report, chain: [], signerMeta: {}, byteRange: [], certCount: 0 };
  }
  const { byteRange, contents, meta } = sig;

  // 被签名内容 = ByteRange 两个区间拼接
  const content = new Uint8Array(byteRange[1] + byteRange[3]);
  content.set(rawBytes.slice(byteRange[0], byteRange[0] + byteRange[1]), 0);
  content.set(rawBytes.slice(byteRange[2], byteRange[2] + byteRange[3]), byteRange[1]);

  let cms;
  try {
    cms = parseSignedData(contents);
  } catch (e) {
    fail('cms', e.message);
    return { report, chain: [], signerMeta: meta, byteRange, certCount: 0 };
  }
  const certs = cms.certsRaw.map(parseCert);
  const signerInfo = cms.signerInfo ? parseSignerInfo(cms.signerInfo) : null;

  const chain = [];
  for (const c of certs) {
    const spkiDer = new Uint8Array(c.spki.toBER(false));
    const fullDer = new Uint8Array(c.certEl.toBER(false));
    chain.push({
      subject: nameToString(c.subject),
      issuer: nameToString(c.issuer),
      serial: bytesToHex(c.serial),
      validFrom: parseTime(c.validity),
      validTo: parseTime(c.validity.valueBlock.value[1]),
      ext: parseExtensions(c.extensions),
      spkiDer,
      tbsRaw: c.tbsRaw,
      sigRaw: c.sigRaw,
      isSelfSigned: nameToString(c.subject) === nameToString(c.issuer),
      sha256: bytesToHex(await sha256(fullDer)).toUpperCase(),
    });
  }

  // ① PDF 数字签名 + ② messageDigest
  let leaf = null;
  if (signerInfo && signerInfo.sid) {
    if (signerInfo.sid.type === 'issuerAndSerial') {
      leaf = chain.find(c => c.serial === signerInfo.sid.serial) || null;
    } else {
      leaf = chain.find(c => c.ext.ski === signerInfo.sid.value) || null;
    }
  }
  if (!leaf) {
    fail('pdf_sig', '未找到签名者(终端)证书');
  } else {
    try {
      const mdAttr = parseMessageDigest(signerInfo);
      if (mdAttr) {
        const attrHex = bytesToHex(mdAttr);
        const calcHex = bytesToHex(await sha256(content));
        ok('messageDigest', `签名属性=${attrHex}；重新计算(覆盖 ByteRange 内容 SHA-256)=${calcHex}；一致=${attrHex.toLowerCase() === calcHex.toLowerCase()}`);
      } else {
        fail('messageDigest', '签名属性中无 messageDigest');
      }
      const verifyData = signerInfo.signedAttrsDer || content;
      const good = await verifyRSA(leaf.spkiDer, signerInfo.signature, verifyData, forcePureJs);
      ok('pdf_sig', `算法=${signerInfo.sigAlgoOid}（sha256WithRSAEncryption），覆盖对象=${signerInfo.signedAttrsDer ? 'SignedAttributes(DER)' : 'ByteRange 内容'}，验证=${good ? '有效' : '无效'}`);
    } catch (e) {
      fail('pdf_sig', '验证异常: ' + e.message);
    }
  }

  // 链结构：根 / 中级 / 终端
  const root = chain.find(c => c.isSelfSigned) || null;
  const bySubject = {};
  chain.forEach(c => { bySubject[c.subject] = c; });
  const terminal = leaf || null;
  const intermediate = terminal && !terminal.isSelfSigned ? bySubject[terminal.issuer] || null : null;

  // ③ 终端 ← 中级
  if (terminal && intermediate) {
    try {
      const g = await verifyRSA(intermediate.spkiDer, terminal.sigRaw, terminal.tbsRaw, forcePureJs);
      ok('leaf_by_inter', `以中级证书公钥验证终端证书签名：终端[${terminal.subject}] ← 中级[${intermediate.subject}] → ${g ? '有效' : '无效'}`);
    } catch (e) { fail('leaf_by_inter', e.message); }
  } else if (terminal) {
    fail('leaf_by_inter', '未找到签发中级证书');
  }

  // ④ 中级 ← 根
  if (intermediate && root) {
    try {
      const g = await verifyRSA(root.spkiDer, intermediate.sigRaw, intermediate.tbsRaw, forcePureJs);
      ok('inter_by_root', `以根证书公钥验证中级证书签名：中级[${intermediate.subject}] ← 根[${root.subject}] → ${g ? '有效' : '无效'}`);
    } catch (e) { fail('inter_by_root', e.message); }
  } else if (intermediate) {
    fail('inter_by_root', '未找到根证书');
  }

  // ⑤ AKI/SKI 链式关联
  const links = [];
  if (terminal && intermediate) {
    links.push({ name: '终端→中级', aki: terminal.ext.aki, ski: intermediate.ext.ski, ok: !!terminal.ext.aki && terminal.ext.aki === intermediate.ext.ski });
  }
  if (intermediate && root) {
    links.push({ name: '中级→根', aki: intermediate.ext.aki, ski: root.ext.ski, ok: !!intermediate.ext.aki && intermediate.ext.aki === root.ext.ski });
  }
  if (links.length) {
    ok('aki_ski', links.map(l => `${l.name}: 子AKI=${l.aki}，父SKI=${l.ski}，一致=${l.ok}`).join('；'));
  } else {
    ok('aki_ski', '无三级结构可比对');
  }

  // ⑥ 根证书与可信根指纹一致性
  if (root) {
    const match = root.sha256.toUpperCase() === (rootSha256 || '').toUpperCase();
    ok('root_trust', `根[${root.subject}] SHA-256=${root.sha256.toUpperCase()}；可信根=${(rootSha256 || '-').toUpperCase()}；一致=${match}`);
  } else {
    fail('root_trust', '无根证书可比对');
  }

  const role = c => (c === root ? '根证书' : c === intermediate ? '中级证书' : c === terminal ? '终端证书(签名者)' : '');
  const chainOut = chain.map(c => ({
    role: role(c),
    subject: c.subject,
    issuer: c.issuer,
    serial: c.serial,
    validFrom: c.validFrom,
    validTo: c.validTo,
    isCA: c.ext.basicConstraints ? c.ext.basicConstraints.ca : null,
    pathlen: c.ext.basicConstraints ? c.ext.basicConstraints.pathlen : null,
    sha256: c.sha256,
    isSelfSigned: c.isSelfSigned,
    ski: c.ext.ski,
    aki: c.ext.aki,
  }));

  return {
    report,
    chain: chainOut,
    signerMeta: {
      reason: meta.reason, location: meta.location, contact: meta.contact,
      time: meta.m, filter: meta.filter, subFilter: meta.subFilter,
    },
    byteRange,
    certCount: chain.length,
  };
}

/**
 * 便捷封装：直接传 File 对象（<input type="file"> 的 files[0]）
 * @param {File} file
 * @param {object} [options]
 */
export async function verifyPdfSignatureByFile(file, options) {
  const buf = await file.arrayBuffer();
  return verifyPdfSignature(new Uint8Array(buf), options);
}

/* 默认导出 */
export default { verifyPdfSignature, verifyPdfSignatureByFile };
